import * as React from 'react';
import { ElementLoader } from 'ui';
import { AppSetting } from 'dependencies/appsettings';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { downloadFile } from 'app/infrastructure/platform/file';
import { STATE } from 'app/constants';
import { SpinLoader } from 'app/components';
import { actionCreators } from './actions';
import { BowtieInstanceState, BowtieStoreState } from './types-store';
import { BowtieExternalProps, BowtieCase, BowtieApp, BowtieTooltipType, DraftConnections, BowtieTooltipOptions } from './types';
import { BowtieOverlay } from 'app/area/bowtie/overlay/bowtie-overlay';
import { BowtieTooltip } from 'app/area/bowtie/tooltip/bowtie-tooltip';
import { mapDraftConnections } from './mapping/map-draft-connections';
import { bowtieTranslations } from './constants';
import { BowtieDisplayMode } from './external-types';
import { getDraftId } from './selectors';

import styles from './bowtie.scss';
import { BowtieFilters } from 'app/area/bowtie/filters/bowtie-filters';
import { SEQUENCE_DIRECTION } from 'app/area/case/features/connected-cases/sequence';
import { CaseViewModalContextType, CaseViewModalContext }  
from 'app/area/case/components/case-view-modal/case-view-modal-context';
import { NavigateToNewDraftInjectedProps, withNavigateToNewDraft } from 'app/area/case/components/enhancers/navigate-to-new-draft';
import { connectionType } from 'app/area/case/case-data-model';

type BowtieProps = DefaultContainerProps & BowtieExternalProps & typeof actionCreators & { instanceState: BowtieInstanceState } 
& CaseViewModalContextType & NavigateToNewDraftInjectedProps;

interface BowtieComponentState {
  caseData: BowtieCase;
  draftConnections: DraftConnections[];
  tooltipOptions: BowtieTooltipOptions;
  left: number;
  right: number;
  top: number;
  type: BowtieTooltipType;
  showFilters: boolean;
  parentCaseId?: number;
  siblingCaseIds?: number[];
  sequenceOrder: number[];
  addNewCaseCaseType?: number;
}

export class BowtieComponent extends React.Component<BowtieProps, BowtieComponentState> {

  public static statePath: string = STATE.bowtie;
  static contextType = CaseViewModalContext;

  private bowtie: BowtieApp;
  private bowtieElement: HTMLElement;
  private bowtieOverlayRef = React.createRef<HTMLDivElement>();

  public componentDidMount() {
    const caseModalcontext = this.context as CaseViewModalContextType;
    const { caseNumber, updateBowtieInstance, instanceKey } = this.props;
    this.loadData();
    caseModalcontext?.registerOnModalClose?.(() => {
       updateBowtieInstance(instanceKey, caseNumber, []);
    });

    window.addEventListener('bowtie:prepare-print', this.handlePreparePrint);
  }

  public componentWillUnmount() {
    if (this.bowtie) {
      this.bowtie.dispose();
      this.bowtie = undefined;
    }
    this.bowtieElement = undefined;
    this.props.clean(this.props.instanceKey);
    window.removeEventListener('bowtie:prepare-print', this.handlePreparePrint);
  }

  private handlePreparePrint = () => {
    this.drawBowtie(true);
  };
  public componentDidUpdate(prevProps: BowtieProps) {
    const {
      caseDataModel, draftData, casesRequiringUpdate, requestId, changeConnectionSequenceId
    } = this.props?.instanceState || {};
    const { caseNumber } = this.props;
    if ((!!caseNumber && caseNumber !== prevProps.caseNumber) || 
      (prevProps.instanceState?.requestId !== undefined && requestId !== prevProps.instanceState?.requestId)) {
      this.loadData();

    } else if (caseDataModel !== prevProps.instanceState?.caseDataModel ||
      draftData !== prevProps.instanceState?.draftData ||
      casesRequiringUpdate !== prevProps.instanceState?.casesRequiringUpdate || 
      changeConnectionSequenceId !== prevProps.instanceState?.changeConnectionSequenceId) {

      this.drawBowtie(false);
    }
  }

  private loadData = async () => {

    const { caseNumber, connectedDrafts, loadBowtie, instanceKey } = this.props;
    const { bowtieLayout } = this.props.instanceState || {};
    if (!caseNumber && caseNumber !== 0) {
      return;
    }

    const draftId = getDraftId(connectedDrafts);

    loadBowtie(bowtieLayout, caseNumber, draftId, instanceKey);
  };

  private connectCase = (caseNumber: number, connectionType: number) => {
    this.hideTooltip();
    this.props.addConnection(getDraftId(this.props.connectedDrafts), caseNumber, connectionType, this.props.instanceKey,
      this.props.instanceState?.draftEtag);
  };

  private disconnectCase = (caseNumber: number, connectionType: number) => {
    this.hideTooltip();
    this.props.removeConnection(getDraftId(this.props.connectedDrafts), caseNumber, connectionType, this.props.instanceKey,
      this.props.instanceState?.draftEtag);
  };

  private showTooltip = (caseData: BowtieCase, draftConnections: DraftConnections[],
    tooltipOptions: BowtieTooltipOptions,
    left: number, 
    right: number, 
    top: number, 
    parentCaseId: number, 
    siblingCaseIds: number[],
    sequenceOrder: number[]
  ) => {
    const { loadEvaluation } = this.props;
    const { inspectionEvaluations } = this.props.instanceState;

    const caseNumber = caseData.caseNumber;

    if (tooltipOptions.showEvaluations &&
      !inspectionEvaluations.evaluationStatuses[caseNumber] &&
      !inspectionEvaluations.fetching.includes(caseNumber)) {
      loadEvaluation(caseNumber, this.props.instanceKey);
    }

    this.setState({
      caseData, draftConnections, tooltipOptions, left, right, top, type: BowtieTooltipType.caseData,
      parentCaseId, siblingCaseIds, sequenceOrder
    });
  };

  private showMeatball = (caseData: BowtieCase, draftConnections: DraftConnections[], left: number, right: number, top: number, 
    addNewCaseCaseType?: number
  ) => {
    const type = addNewCaseCaseType ? BowtieTooltipType.addNewCase : BowtieTooltipType.connections;
    this.setState({
      caseData, draftConnections, left, right, top, type, addNewCaseCaseType
    });
  };

  private showFilters = () => {
    this.setState({
      showFilters: true
    });
  };

  private hideFilters = () => {
    this.setState({
      showFilters: false
    });
  };

  private hideTooltip = () => {
    this.setState({
      caseData: undefined
    });
  };

  private addBowtieCanvas = (elem: HTMLDivElement | null) => {
    if (!elem) { return; }
    void this.initializeBowtieCanvas(elem);
  };

  private initializeBowtieCanvas = async (elem: HTMLDivElement) => {
    const { Canvas } = await import(/* webpackChunkName: "bowtie" */ 'app/components/bowtie/components/canvas');
    this.bowtie = new Canvas(elem, AppSetting.ApplicationUrl, {
      showTooltip: this.showTooltip,
      showMeatball: this.showMeatball,
      hideTooltip: this.hideTooltip,
      updateSequence: this.updateSequence,
      addNewCase: this.addNewCase
    },
      {
        downloadFile: downloadFile,
        openFilters: this.showFilters
      },
      styles);
    this.drawBowtie(true);
    this.bowtieElement = elem;
  };

  private drawBowtie = (resetZoom: boolean) => {

    const { fetchingBowtie, caseDataModel, bowtieLayout, caseTemplate, draftData, casesRequiringUpdate, 
      changeConnectionSequenceId } = this.props.instanceState;

    if (!this.bowtie || fetchingBowtie || !caseDataModel) {
      return;
    }
    const connectMode = !!draftData;
    const dataModel = {
      caseDataModel: {
        ...caseDataModel,
        ...bowtieLayout
      },
      caseTemplate,
      draftConnections: mapDraftConnections(caseDataModel, draftData, casesRequiringUpdate),
      changeConnectionSequenceId,
      displayMode: this.props.displayMode
    };

    this.bowtie.draw(dataModel, resetZoom, connectMode);
  };

  private updateSequence = ({caseId, siblingCaseIds, parentCaseId, sequenceOrder, steps}) => {
    const { changeSequenceBowtie, instanceKey } = this.props;
    changeSequenceBowtie({
      instanceKey,
      parentCaseId,
      caseId,
      siblingCaseIds,
      sequenceOrder,
      allowWarnings: false,
      steps
    });
  };

  private changeSequence = (direction: SEQUENCE_DIRECTION) => {
    const { changeSequenceBowtie, instanceKey } = this.props;
    const { caseData, siblingCaseIds, parentCaseId, sequenceOrder } = this.state;
    const steps = direction === SEQUENCE_DIRECTION.UP ? -1 : 1;
    changeSequenceBowtie({
      instanceKey,
      parentCaseId,
      caseId: caseData.caseNumber,
      siblingCaseIds,
      sequenceOrder,
      allowWarnings: false,
      steps
    });
  };

  private addNewCase = (entityId: number, caseType: number) => {
    const { navigateToNewDraft } = this.props;
    if (caseType) {
      navigateToNewDraft({ caseType, entityId, connectedDrafts: [], connectionTypeId: connectionType.superior });
    }
  };

  public render() {
    const { translation, locale, caseNumber, connectedDrafts, footer, displayMode, instanceKey } = this.props;
    const { caseDataModel, fetchingBowtie, inspectionEvaluations,
      connectionTypes, connectedCases,
      draftData, caseTemplate, changeConnectionSequenceId, stateForCaseIds } = this.props?.instanceState || {};
    const draftId = getDraftId(connectedDrafts);
    const loadConnectedCases = (caseId: number, type: number) => {
      this.props.loadConnectedCases(caseId, type, instanceKey);
    };

    return <BowtieOverlay
      displayOverlay={displayMode === BowtieDisplayMode.fullscreen}
      caseNumber={caseNumber}
      connectedDrafts={connectedDrafts}
      translation={translation}
      className={changeConnectionSequenceId ? styles.changingConnectionSequence : ''}
      >
      {fetchingBowtie
        ? displayMode === BowtieDisplayMode.fullscreen ? <SpinLoader /> : <ElementLoader />
        : <div className={`${styles.bowtie} ${displayMode !== BowtieDisplayMode.fullscreen
          ? styles.compactMode
          : ''}`}
          ref={this.addBowtieCanvas}>
          {!!this?.state?.showFilters && <BowtieFilters onClose={this.hideFilters} locale={locale} instanceKey={instanceKey} />}
          {caseDataModel && 
          <div ref={this.bowtieOverlayRef}>
          <BowtieTooltip
            draftId={draftId}
            draftData={draftData}
            connectionTypes={connectionTypes}
            connectCase={this.connectCase}
            loadConnectedCases={loadConnectedCases}
            disconnectCase={this.disconnectCase}
            componentElement={this.bowtieElement}
            caseTemplate={caseTemplate}
            translation={translation}
            inspectionEvaluations={inspectionEvaluations}
            connectedCases={connectedCases}
            otherConnections={caseDataModel.otherConnections}
            siblingCaseIds={this.state?.siblingCaseIds}
            parentCaseId={this.state?.parentCaseId}
            changeSequence={this.changeSequence}
            bowtieOverlayRef={this.bowtieOverlayRef}
            changeConnectionSequenceId={changeConnectionSequenceId}
            stateForCaseIds={stateForCaseIds}
            addNewCase={this.addNewCase}
            {...this.state} />
           </div>
          }
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>}
    </BowtieOverlay>;
  }

}
const selectors = {
  instanceState: (state: BowtieStoreState, externalProps: BowtieExternalProps) => {
    return state.instances?.[externalProps.instanceKey] || {};
  }
};

const container = withNavigateToNewDraft(containerFactory<BowtieExternalProps>
  (BowtieComponent, actionCreators, selectors, bowtieTranslations), true);

export { container as Bowtie };
